import { Info, Pencil } from "lucide-react";
import { DateTime } from "luxon";
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { fr } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import { ClimateDataGranularity } from "@/api/climate-data/types";

/* -------------------------------------------------------------------------- */
/* Constantes & helpers partagés                                              */
/* -------------------------------------------------------------------------- */

export const PRIMARY_800 = "#146d42";

export const Card = tw.div`bg-white rounded-xl border border-gray-200 shadow-sm`;

export const pad = (n: number) => String(n).padStart(2, "0");

export const withTime = (date: Date, hhmm: string) => {
  const [h, m] = hhmm.split(":").map((v) => parseInt(v, 10));
  const next = new Date(date);
  next.setHours(h, m, 0, 0);
  return next;
};

export const formatDateDDMMYYYY = (date: Date) =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

export const formatPeriod = (iso: string) => {
  const d = DateTime.fromISO(iso).setLocale("fr");
  return d.toFormat("dd LLL HH:mm");
};

/** Jour seul, ex. « 08 juil. » (sans l'heure). */
export const formatDayLabel = (iso: string) =>
  DateTime.fromISO(iso).setLocale("fr").toFormat("dd LLL");

/** Heure seule, ex. « 00:00 ». */
export const formatTimeLabel = (iso: string) =>
  DateTime.fromISO(iso).setLocale("fr").toFormat("HH:mm");

/** Vrai si les deux instants tombent le même jour calendaire (zone locale). */
export const isSameCalendarDay = (fromIso: string, toIso: string) =>
  DateTime.fromISO(fromIso).hasSame(DateTime.fromISO(toIso), "day");

export const formatDuration = (fromIso: string, toIso: string) => {
  const diff = DateTime.fromISO(toIso).diff(DateTime.fromISO(fromIso), [
    "hours",
    "minutes",
  ]);
  const hours = Math.floor(diff.hours);
  const minutes = Math.max(0, Math.round(diff.minutes));
  if (hours === 0) return `${minutes}min`;
  return minutes > 0
    ? `${hours}h${String(minutes).padStart(2, "0")}`
    : `${hours}h`;
};

/* -------------------------------------------------------------------------- */
/* InfoTooltip — bulle d'aide repositionnée via portal                         */
/* -------------------------------------------------------------------------- */

export const InfoTooltip = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });
  const [arrowLeft, setArrowLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !tooltipRef.current?.contains(target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !tooltipRef.current) return;
    const btn = buttonRef.current.getBoundingClientRect();
    const tooltipW = tooltipRef.current.offsetWidth;
    const tooltipH = tooltipRef.current.offsetHeight;
    const padding = 12;

    const btnCenterX = btn.left + btn.width / 2;
    let left = btnCenterX - tooltipW / 2;
    left = Math.max(
      padding,
      Math.min(left, window.innerWidth - tooltipW - padding)
    );

    const top = btn.top - tooltipH - 8;

    setArrowLeft(btnCenterX - left);
    setStyle({
      position: "fixed",
      left,
      top,
      zIndex: 9999,
      visibility: "visible",
    });
  }, [open]);

  return (
    <div ref={containerRef} tw="inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setStyle({ visibility: "hidden" });
          setOpen((v) => !v);
        }}
        tw="inline-flex items-center justify-center"
      >
        <Info tw="w-3 h-3 text-gray-400" />
      </button>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            tw="w-52 bg-gray-800 text-white text-xs rounded-xl px-3 py-2.5 shadow-lg leading-relaxed"
            style={style}
          >
            {text}
            <div
              tw="absolute top-full border-4 border-transparent border-t-gray-800"
              style={{ left: arrowLeft }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

/**
 * Petite carte statistique (icône + label + valeur) réutilisée par toutes les
 * vues climatiques. `infoText` ajoute une bulle d'aide optionnelle.
 */
export const SmallStatCard = ({
  icon,
  iconBg,
  label,
  infoText,
  value,
  valueColor,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  infoText?: string;
  value: string;
  valueColor: string;
}) => (
  <Card tw="flex-1 flex items-center gap-2 px-3 py-3 min-w-0">
    <div
      css={[
        tw`w-10 h-10 rounded-full flex items-center justify-center shrink-0`,
      ]}
      style={{ backgroundColor: iconBg }}
    >
      {icon}
    </div>
    <div tw="flex flex-col min-w-0">
      <p tw="text-xs text-gray-500 flex items-center gap-1">
        {label}
        {infoText && <InfoTooltip text={infoText} />}
      </p>
      <p css={[tw`text-xl font-bold`]} style={{ color: valueColor }}>
        {value}
      </p>
    </div>
  </Card>
);

/* -------------------------------------------------------------------------- */
/* Plages temporelles — presets + plage personnalisée                          */
/* -------------------------------------------------------------------------- */

export type Preset = "today" | "yesterday" | "last7days" | "custom";

const PRESETS: Preset[] = ["today", "yesterday", "last7days", "custom"];

export type AppliedRange = {
  from: string;
  to: string;
  granularity: ClimateDataGranularity;
};

type CustomDraft = {
  fromDate?: Date;
  toDate?: Date;
  fromTime: string;
  toTime: string;
};

const DEFAULT_CUSTOM_DRAFT: CustomDraft = {
  fromDate: undefined,
  toDate: undefined,
  fromTime: "00:00",
  toTime: "23:59",
};

const presetToRange = (preset: Exclude<Preset, "custom">): AppliedRange => {
  const now = DateTime.now();
  switch (preset) {
    case "today":
      return {
        from: now.startOf("day").toUTC().toISO()!,
        to: now.toUTC().toISO()!,
        granularity: "hour",
      };
    case "yesterday": {
      const yesterday = now.minus({ days: 1 });
      return {
        from: yesterday.startOf("day").toUTC().toISO()!,
        to: yesterday.endOf("day").toUTC().toISO()!,
        granularity: "hour",
      };
    }
    case "last7days":
      return {
        from: now.startOf("day").minus({ days: 7 }).toUTC().toISO()!,
        to: now.toUTC().toISO()!,
        granularity: "day",
      };
  }
};

const customGranularity = (from: Date, to: Date): ClimateDataGranularity => {
  const days = (to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000);
  return days <= 2 ? "hour" : "day";
};

/**
 * Pilote l'état de sélection de plage (preset + plage perso) commun aux vues
 * climatiques. Expose la plage appliquée à requêter ainsi que les handlers
 * consommés par {@link ClimateRangeControls}.
 */
export const useClimateRange = () => {
  const [preset, setPreset] = useState<Preset>("today");
  const [appliedRange, setAppliedRange] = useState<AppliedRange>(() =>
    presetToRange("today")
  );
  const [customDraft, setCustomDraft] =
    useState<CustomDraft>(DEFAULT_CUSTOM_DRAFT);
  const [isCustomExpanded, setIsCustomExpanded] = useState<boolean>(true);

  const handlePresetChange = (next: Preset) => {
    setPreset(next);
    if (next !== "custom") {
      setCustomDraft(DEFAULT_CUSTOM_DRAFT);
      setIsCustomExpanded(true);
      setAppliedRange(presetToRange(next));
    }
  };

  const handleCustomDatesChange = (selected: { from?: Date; to?: Date }) => {
    setCustomDraft((prev) => ({
      ...prev,
      fromDate: selected.from,
      toDate: selected.to,
    }));
  };

  const handleFromTimeChange = (hhmm: string) =>
    setCustomDraft((prev) => ({ ...prev, fromTime: hhmm }));

  const handleToTimeChange = (hhmm: string) =>
    setCustomDraft((prev) => ({ ...prev, toTime: hhmm }));

  const handleValidateCustom = () => {
    if (!customDraft.fromDate || !customDraft.toDate) return;
    const from = withTime(customDraft.fromDate, customDraft.fromTime);
    const to = withTime(customDraft.toDate, customDraft.toTime);
    setAppliedRange({
      from: DateTime.fromJSDate(from).toUTC().toISO()!,
      to: DateTime.fromJSDate(to).toUTC().toISO()!,
      granularity: customGranularity(from, to),
    });
    setIsCustomExpanded(false);
  };

  return {
    preset,
    appliedRange,
    customDraft,
    isCustomExpanded,
    handlePresetChange,
    handleCustomDatesChange,
    handleFromTimeChange,
    handleToTimeChange,
    handleValidateCustom,
    setIsCustomExpanded,
  };
};

export type ClimateRange = ReturnType<typeof useClimateRange>;

const PresetSelector = ({
  preset,
  onChange,
}: {
  preset: Preset;
  onChange: (p: Preset) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div tw="grid grid-cols-2 gap-2">
      {PRESETS.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          css={[
            tw`px-2 py-2 rounded-lg border text-sm font-medium transition`,
            preset === p
              ? tw`bg-primary-800 text-white border-primary-800`
              : tw`bg-white text-gray-700 border-gray-200 active:bg-gray-50`,
          ]}
        >
          {t(`meteo.climateView.presets.${p}`)}
        </button>
      ))}
    </div>
  );
};

const EndpointCartouche = ({
  label,
  date,
  time,
  onTimeChange,
}: {
  label: string;
  date: Date | undefined;
  time: string;
  onTimeChange: (hhmm: string) => void;
}) => (
  <div tw="flex flex-col gap-1.5 border border-gray-200 rounded-lg p-2.5 flex-1 min-w-0">
    <span tw="text-xs text-gray-500 font-medium">{label}</span>
    <span
      css={[
        tw`text-sm font-semibold`,
        date ? tw`text-gray-800` : tw`text-gray-400`,
      ]}
    >
      {date ? formatDateDDMMYYYY(date) : "—"}
    </span>
    <input
      type="time"
      value={time}
      onChange={(e) => onTimeChange(e.target.value)}
      tw="border border-gray-200 rounded-md px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-800/30"
    />
  </div>
);

const CustomRangePicker = ({
  today,
  draft,
  onDatesChange,
  onFromTimeChange,
  onToTimeChange,
  onValidate,
}: {
  today: Date;
  draft: CustomDraft;
  onDatesChange: (range: { from?: Date; to?: Date }) => void;
  onFromTimeChange: (hhmm: string) => void;
  onToTimeChange: (hhmm: string) => void;
  onValidate: () => void;
}) => {
  const { t } = useTranslation();
  const canValidate = !!draft.fromDate && !!draft.toDate;

  return (
    <Card tw="p-3 flex flex-col gap-3">
      <div
        style={
          {
            ["--rdp-accent-color" as never]: PRIMARY_800,
            ["--rdp-accent-background-color" as never]: "#dbfdec",
            ["--rdp-day-height" as never]: "36px",
            ["--rdp-day-width" as never]: "36px",
            ["--rdp-day_button-height" as never]: "34px",
            ["--rdp-day_button-width" as never]: "34px",
          } as React.CSSProperties
        }
      >
        <DayPicker
          mode="range"
          locale={fr}
          weekStartsOn={1}
          selected={{ from: draft.fromDate, to: draft.toDate }}
          onSelect={(selected) => onDatesChange(selected ?? {})}
          disabled={{ after: today }}
          numberOfMonths={1}
        />
      </div>

      <div tw="flex items-stretch gap-3 pt-3 border-t border-gray-100">
        <EndpointCartouche
          label={t("meteo.climateView.customRange.from")}
          date={draft.fromDate}
          time={draft.fromTime}
          onTimeChange={onFromTimeChange}
        />
        <EndpointCartouche
          label={t("meteo.climateView.customRange.to")}
          date={draft.toDate}
          time={draft.toTime}
          onTimeChange={onToTimeChange}
        />
      </div>

      <button
        type="button"
        disabled={!canValidate}
        onClick={onValidate}
        css={[
          tw`w-full px-3 py-2.5 rounded-lg text-sm font-semibold transition`,
          canValidate
            ? tw`bg-primary-800 text-white active:opacity-80`
            : tw`bg-gray-100 text-gray-400 cursor-not-allowed`,
        ]}
      >
        {t("meteo.climateView.customRange.validate")}
      </button>
    </Card>
  );
};

const CustomRangeSummary = ({
  fromDate,
  fromTime,
  toDate,
  toTime,
  onEdit,
}: {
  fromDate: Date;
  fromTime: string;
  toDate: Date;
  toTime: string;
  onEdit: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      tw="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer active:opacity-80 transition"
    >
      <div tw="flex flex-col gap-1 min-w-0">
        <span tw="text-xs text-gray-500">
          {t("meteo.climateView.customRange.label")}
        </span>
        <div tw="flex flex-col gap-0.5 text-sm text-gray-800">
          <span tw="flex items-baseline gap-2 truncate">
            <span tw="text-xs text-gray-400 font-medium w-6 shrink-0">
              {t("meteo.climateView.customRange.summaryFrom")}
            </span>
            <span tw="font-semibold">
              {formatDateDDMMYYYY(fromDate)}
              <span tw="text-gray-400"> · </span>
              {fromTime}
            </span>
          </span>
          <span tw="flex items-baseline gap-2 truncate">
            <span tw="text-xs text-gray-400 font-medium w-6 shrink-0">
              {t("meteo.climateView.customRange.summaryTo")}
            </span>
            <span tw="font-semibold">
              {formatDateDDMMYYYY(toDate)}
              <span tw="text-gray-400"> · </span>
              {toTime}
            </span>
          </span>
        </div>
      </div>
      <Pencil tw="w-4 h-4 text-gray-400 shrink-0" />
    </Card>
  );
};

/**
 * Bloc complet de sélection de plage (presets + calendrier perso replié/déplié),
 * piloté par {@link useClimateRange}. Partagé par toutes les vues climatiques.
 */
export const ClimateRangeControls = ({ range }: { range: ClimateRange }) => {
  const today = useMemo(() => new Date(), []);
  const {
    preset,
    customDraft,
    isCustomExpanded,
    handlePresetChange,
    handleCustomDatesChange,
    handleFromTimeChange,
    handleToTimeChange,
    handleValidateCustom,
    setIsCustomExpanded,
  } = range;

  return (
    <Fragment>
      <PresetSelector preset={preset} onChange={handlePresetChange} />

      {preset === "custom" &&
        (isCustomExpanded ? (
          <CustomRangePicker
            today={today}
            draft={customDraft}
            onDatesChange={handleCustomDatesChange}
            onFromTimeChange={handleFromTimeChange}
            onToTimeChange={handleToTimeChange}
            onValidate={handleValidateCustom}
          />
        ) : (
          customDraft.fromDate &&
          customDraft.toDate && (
            <CustomRangeSummary
              fromDate={customDraft.fromDate}
              fromTime={customDraft.fromTime}
              toDate={customDraft.toDate}
              toTime={customDraft.toTime}
              onEdit={() => setIsCustomExpanded(true)}
            />
          )
        ))}
    </Fragment>
  );
};
